import { debounce } from 'lodash';
import { useMemo, useState, useEffect } from 'react';

import {
  Radio,
  Stack,
  TextField,
  FormLabel,
  RadioGroup,
  FormControl,
  Autocomplete,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { List, ListEntityType } from 'src/services/lists/lists.types';
import { useGetListsPaginatedQuery } from 'src/services/lists/lists.service';

export type ImportListMode = 'none' | 'existing' | 'new';

/** The choices made on the preview step, before the rows are sent. */
export interface ImportOptionsValue {
  onDuplicate: 'enrich' | 'skip';
  listMode: ImportListMode;
  existingList: List | null;
  newListName: string;
}

export const DEFAULT_IMPORT_OPTIONS: ImportOptionsValue = {
  onDuplicate: 'enrich',
  listMode: 'none',
  existingList: null,
  newListName: '',
};

/** Whether the list choice is complete enough to import with. */
export const importOptionsReady = (value: ImportOptionsValue): boolean => {
  if (value.listMode === 'existing') return Boolean(value.existingList);
  if (value.listMode === 'new') return Boolean(value.newListName.trim());
  return true;
};

type Props = {
  value: ImportOptionsValue;
  onChange: (value: ImportOptionsValue) => void;
  listEntityType: ListEntityType;
  listsEnabled: boolean;
  /** Set when a super admin imports on an organisation's behalf: its lists are the ones offered. */
  organizationId?: string;
};

export default function ImportOptions({ value, onChange, listEntityType, listsEnabled, organizationId }: Props) {
  const { t } = useTranslate();
  const [search, setSearch] = useState('');

  const onSearch = useMemo(() => debounce((text: string) => setSearch(text), 400), []);
  useEffect(() => () => onSearch.cancel(), [onSearch]);

  const { currentData: lists, isFetching } = useGetListsPaginatedQuery(
    { page: 1, per_page: 20, entity_type: listEntityType, search_query: search, organization_id: organizationId },
    { skip: !listsEnabled || value.listMode !== 'existing' }
  );

  // A system list (Website Registrations) is filled by its own flow and takes nothing else.
  const options = useMemo(() => (lists?.results ?? []).filter((list) => !list.system_type), [lists]);

  const update = (patch: Partial<ImportOptionsValue>) => onChange({ ...value, ...patch });

  return (
    <Stack spacing={2.5}>
      <FormControl>
        <FormLabel>{t('csv_import.options.duplicates_label')}</FormLabel>
        <RadioGroup
          row
          value={value.onDuplicate}
          onChange={(event) => update({ onDuplicate: event.target.value as ImportOptionsValue['onDuplicate'] })}
        >
          <FormControlLabel value="enrich" control={<Radio size="small" />} label={t('csv_import.options.duplicates_enrich')} />
          <FormControlLabel value="skip" control={<Radio size="small" />} label={t('csv_import.options.duplicates_skip')} />
        </RadioGroup>
        <FormHelperText sx={{ mx: 0 }}>
          {value.onDuplicate === 'enrich'
            ? t('csv_import.options.duplicates_enrich_help')
            : t('csv_import.options.duplicates_skip_help')}
        </FormHelperText>
      </FormControl>

      {listsEnabled && (
        <FormControl>
          <FormLabel>{t('csv_import.options.list_label')}</FormLabel>
          <RadioGroup
            row
            value={value.listMode}
            onChange={(event) => update({ listMode: event.target.value as ImportListMode })}
          >
            <FormControlLabel value="none" control={<Radio size="small" />} label={t('csv_import.options.list_none')} />
            <FormControlLabel value="existing" control={<Radio size="small" />} label={t('csv_import.options.list_existing')} />
            <FormControlLabel value="new" control={<Radio size="small" />} label={t('csv_import.options.list_new')} />
          </RadioGroup>

          {value.listMode === 'existing' && (
            <Autocomplete
              sx={{ mt: 1, maxWidth: 420 }}
              size="small"
              options={options}
              loading={isFetching}
              value={value.existingList}
              getOptionLabel={(list) => list.name}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              // The server already filtered by the search text.
              filterOptions={(all) => all}
              onChange={(_, list) => update({ existingList: list })}
              onInputChange={(_, text, reason) => {
                if (reason === 'input') onSearch(text);
              }}
              renderInput={(params) => <TextField {...params} label={t('csv_import.options.list_pick')} />}
            />
          )}

          {value.listMode === 'new' && (
            <TextField
              sx={{ mt: 1, maxWidth: 420 }}
              size="small"
              label={t('csv_import.options.list_name')}
              value={value.newListName}
              onChange={(event) => update({ newListName: event.target.value })}
              inputProps={{ maxLength: 255 }}
            />
          )}
        </FormControl>
      )}
    </Stack>
  );
}
